import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {connect, ConnectedProps} from "react-redux";

import {useAppDispatch, useRefreshInterval} from "../../../../store/hooks";
import {IDateRangeGState, ILoadingComponents} from "../../../../interfaces/datesGlobalState.interface";
import {RootState} from "../../../../interfaces/root-state.interface";

import {publisherDataPointsBaseUrl} from "../../../../config/generals";

import TagsTable from "./TagsTable/publisherTagTable"
import DiscrepancyMetrics from "../../common/DiscrepancyMetrics"
import {
    IGraphTimeBucket,
} from "@leap4/entities-discrepancy-common";
import {AlertTypes, apiClient, Grid, IAlertData, setAlert, Paper, Typography, Skeleton, useTheme, GpsFixed as GpsFixedIcon, MuiIcons} from "@leap4/frontend";
import DiscrepancyChart from "../../common/DiscrepancyChart";
import ReportHeader from "../../common/ReportHeader/ReportHeader";
import {urlService} from "../../../routing/urlsService";
import {setIsLoading} from "../../../../store/actions/dateRange.actions";
import {IPublisherDiscPage} from "../interfaces/DataPointDtoMainPage.interface";
import TopDiscrepancyTagsTable, {ITopDiscrepancyTagsResult} from "./TagsTable/topDiscrepancyTagsTable";
import moment from "moment";

const { LocationSearching: LocationSearchingIcon } = MuiIcons;

const mapStateToProps = (state: RootState) => ({
    dates: state.dates,
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IPropsDataPointPage extends PropsFromRedux {
    dates: IDateRangeGState
    setIsLoading: (isLoading: Partial<ILoadingComponents>) => void
    setAlert: (alert: IAlertData) => void
}


const PublisherDataPoint: React.FC<IPropsDataPointPage> = ({
                                                               dates: {startDate, endDate, isLoading},
                                                               setIsLoading,
                                                               setAlert
                                                           }): JSX.Element => {

    const [data, setData] = useState<IPublisherDiscPage>(null);
    const [graphData, setGraphData] = useState<IGraphTimeBucket[]>(null)
    const [topDiscrepancyTagsData, setTopDiscrepancyTagsData] = useState<ITopDiscrepancyTagsResult[]>(null);
    const params = useParams<{ id: string }>()
    const theme = useTheme()
    const dispatch = useAppDispatch();
    useRefreshInterval(() => {
        if (startDate === endDate && startDate === moment().format("YYYY-MM-DD")) {
            fetchGeneralData()
            fetchGraphData()
            fetchTopDiscrepancyTagsData()
        }
    })
    const fetchGeneralData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGeneral: true});
                const baseUrl = `${publisherDataPointsBaseUrl}/${params.id}`;
                const apiResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate
                })

                const apiData: IPublisherDiscPage = apiResponse.data
                setData(apiData)

            } catch (err) {
                setAlert({msg: "Couldnt fetch data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGeneral: false});
            }
        })()

    };

    const fetchGraphData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGraph: true});
                const baseUrl = `${publisherDataPointsBaseUrl}/${params.id}/graph`
                const graphResponse = await apiClient.post(`${baseUrl}`, {startDate, endDate})
                const graphData = graphResponse.data
                setGraphData(graphData)
            } catch (err) {
                setAlert({msg: "Could'nt fetch Graph Data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGraph: false});
            }
        })()
    }

    const fetchTopDiscrepancyTagsData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingTopDiscTagsTable: true})
                const baseUrl = `${publisherDataPointsBaseUrl}/${params.id}/topDiscrepancyTags`
                const topDiscrepancyResponse = await apiClient.post(`${baseUrl}`, {startDate, endDate})
                const topDiscrepancyData = topDiscrepancyResponse.data
                setTopDiscrepancyTagsData(topDiscrepancyData)
            } catch (err) {
                setAlert({msg: "Could'nt fetch Top Discrepancy Table Data", type: AlertTypes.FAILURE, d: dispatch})
            }
            finally {
                setIsLoading({isLoadingTopDiscTagsTable: false});
            }
        })()
    }

    useEffect(fetchGeneralData, [startDate, endDate, params.id])
    useEffect(fetchGraphData, [startDate, endDate, params.id]);
    useEffect(fetchTopDiscrepancyTagsData, [startDate, endDate, params.id]);

    const ReportHeaderWithProps = () => (
        <ReportHeader breadcrumbs={[
            {text: "Publisher Data Points", link: urlService.publisherDataPoints(), icon: <LocationSearchingIcon  />},
            {text: data?.dataPoint?.name || "loading...", icon: <GpsFixedIcon />}
        ]}/>
    )

    if (!isLoading.isLoadingGeneral && !data) return (<>
        <ReportHeaderWithProps/>
        <Typography variant={"overline"}> Error with getting the
            data...</Typography></>)

    const {discrepancy} = (data || {});

    return (
        <>
            <ReportHeaderWithProps/>
            <Grid container justifyContent={"center"}
                  style={{margin: "auto", filter: theme.palette.mode === 'dark'? 'drop-shadow(0px 0px 2px lightgray' : 'drop-shadow(0px 0px 5px lightgray'}} direction={"row"} spacing={2}>
                <>
                    <Grid item sm={12} lg={4}>
                        {!isLoading.isLoadingGeneral && data ?
                            <Paper elevation={5} style={{padding: "15px 0", height: '100%', borderRadius: "16px"}}
                                   sx={{display: 'flex', alignItems: 'center'}}>
                                <Grid container direction={"row"} justifyContent={"space-between"}>
                                    <Grid item xs={12} md={12} height={"100%"}>
                                        <DiscrepancyMetrics
                                            adServersTotals={{
                                                requests: 0,
                                                cost: 0,
                                                impressions: discrepancy.adServerImpressions,
                                                revenue: discrepancy.adServerRevenue
                                            }}
                                            publisherTotals={{
                                                impressions: discrepancy.publisherImpressions,
                                                revenue: discrepancy.publisherRevenue
                                            }}
                                            discrepancy={{
                                                impressions: {
                                                    percent: discrepancy.impressionsDiscPercent,
                                                    value: discrepancy.impressionsDiscValue
                                                },
                                                revenue: {
                                                    percent: discrepancy.revenueDiscPercent,
                                                    value: discrepancy.revenueDiscValue
                                                }
                                            }}
                                            subtitle={(data.dataPoint.adServer as any).name.toString()}
                                            title={data.dataPoint.name.toUpperCase()}

                                        />
                                    </Grid>
                                </Grid>
                            </Paper> : <Skeleton animation={"wave"} sx={{borderRadius: "16px", m:5}} variant={"rounded"} height={"100%"}/>}
                    </Grid>
                    <Grid item sm={12} lg={8} style={{height: '450px', width: '95%'}}>
                        <DiscrepancyChart {...{graphData, startDate, endDate, isLoading: isLoading.isLoadingGraph}} />
                    </Grid>
                    <Grid item xs={12}>
                        {!isLoading.isLoadingGeneral && data ?
                            <TagsTable items={data.tags} loading={isLoading.isLoadingGeneral}
                                       refreshFunc={fetchGeneralData}/> :
                            <Skeleton animation={"wave"} height={"300px"} sx={{borderRadius: "16px", m:5}} variant={"rounded"} />}
                    </Grid>

                    <Grid item xs={12}>
                        {
                            isLoading.isLoadingTopDiscTagsTable? <Skeleton animation={"wave"} height={"300px"} sx={{borderRadius: "16px", m:5}} variant={"rounded"} />:
                                !isLoading.isLoadingTopDiscTagsTable &&topDiscrepancyTagsData.length > 0?
                                    <TopDiscrepancyTagsTable items={topDiscrepancyTagsData} loading={isLoading.isLoadingTopDiscTagsTable}
                                                             refreshFunc={fetchTopDiscrepancyTagsData}/> :
                                    <Grid item xs={12}/>
                        }

                    </Grid>
                </>
            </Grid>
        </>)
}


export default connector(PublisherDataPoint);